import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import apiClient from "../../../shared/API/apiClient";
import { useReportingYear } from "../../../context/ReportingYearContext";
import "../../../styles/pages/submissionTracker.css";
import "../../../styles/components/button.css";
import usePermission from "../../../shared/hooks/usePermission";

const FA_ICON_MAP = {
  "fa-draft":       "fa-solid fa-pen-to-square",
  "fa-submitted":   "fa-solid fa-paper-plane",
  "fa-reviewed":    "fa-solid fa-circle-check",
  "fa-approved":    "fa-solid fa-check-double",
  "fa-locked":      "fa-solid fa-lock",
  "fa-notstarted":  "fa-solid fa-clock",
  "fa-rework":      "fa-solid fa-rotate-left",
  "fa-list":        "fa-solid fa-list",
  "fa-exclamation": "fa-solid fa-circle-exclamation",
  "fa-paperclip":   "fa-solid fa-paperclip",
};

// ── Send Back Modal ────────────────────────────────────────────────────────
const SendBackModal = ({ module, onClose, onConfirm }) => {
  const [comment, setComment] = useState("");
  const [error,   setError]   = useState(false);

  const handleSendBack = () => {
    if (!comment.trim()) { setError(true); return; }
    onConfirm(module, comment);
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal__header">
          <span className="modal__title">Send Back for Corrections</span>
          <button className="modal__close" onClick={onClose}>×</button>
        </div>
        <div className="modal__body">
          <label className="modal__label">
            Comment <span className="modal__required">*</span>
          </label>
          <textarea
            className={`modal__textarea${error ? " modal__textarea--error" : ""}`}
            placeholder="Describe what needs correction..."
            value={comment}
            rows={5}
            onChange={(e) => { setComment(e.target.value); if (e.target.value.trim()) setError(false); }}
          />
          {error && <p className="modal__hint">A comment is required when sending a module back.</p>}
        </div>
        <div className="modal__footer">
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn-sendback" onClick={handleSendBack}>Send Back</button>
        </div>
      </div>
    </div>
  );
};

// ── Main Component ─────────────────────────────────────────────────────────
const ReviewScreen = () => {
  const navigate                       = useNavigate();
  const { state: routeState }          = useLocation();
  const { reportingYear, reportingYearId } = useReportingYear();

  const seedEntityId = routeState?.selectedEntity  ?? "";
  const seedEntities = routeState?.legalEntities   ?? [];
  const seedYearId   = routeState?.reportingYearId ?? reportingYearId;
  const seedYear     = routeState?.reportingYear   ?? reportingYear;

  const [legalEntities,        setLegalEntities]        = useState(seedEntities);
  const [legalEntitiesLoading, setLegalEntitiesLoading] = useState(false);
  const [selectedEntity,       setSelectedEntity]       = useState(seedEntityId);
  const [stageCounts,          setStageCounts]          = useState([]);
  const [modules,              setModules]              = useState([]);
  const [tableLoading,         setTableLoading]         = useState(false);
  const [tableError,           setTableError]           = useState("");
  const [sendBackTarget,       setSendBackTarget]       = useState(null);
  const [markingReviewedId,    setMarkingReviewedId]    = useState(null);

  const buildReviewState = (extras = {}) => ({
    selectedEntity, legalEntities,
    reportingYearId: seedYearId, reportingYear: seedYear, ...extras,
  });

  useEffect(() => {
    if (seedEntities.length > 0) return;
    if (!seedYearId) return;
    setLegalEntitiesLoading(true);
    apiClient
      .get("/api/legal-entities-by-year", { params: { reporting_year_id: seedYearId } })
      .then((res) => setLegalEntities(res.data?.code === 200 ? res.data.data || [] : []))
      .catch(() => setLegalEntities([]))
      .finally(() => setLegalEntitiesLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedYearId]);

  const fetchReviewData = useCallback(async (entityId) => {
    if (!seedYearId || !entityId) return;
    setTableLoading(true);
    setTableError("");
    setStageCounts([]);
    setModules([]);

    const params = { reporting_year_id: seedYearId, legal_entity_id: entityId };
    try {
      const [statusRes, submissionsRes] = await Promise.all([
        apiClient.get("/api/review-status",      { params }),
        apiClient.get("/api/review-submissions", { params }),
      ]);

      const rawStatus = statusRes.data?.data;
      if (Array.isArray(rawStatus)) {
        setStageCounts(rawStatus);
      } else if (rawStatus && typeof rawStatus === "object") {
        setStageCounts([
          { status_name: "Awaiting Review", count: rawStatus.awaiting_review ?? 0 },
          { status_name: "Reviewed",        count: rawStatus.reviewed        ?? 0 },
          { status_name: "Approved",        count: rawStatus.approved        ?? 0 },
          { status_name: "Locked",          count: rawStatus.locked          ?? 0 },
        ]);
      }
      setModules(submissionsRes.data?.data ?? submissionsRes.data ?? []);
    } catch (err) {
      console.error("ReviewScreen fetch error:", err);
      setTableError("Failed to load review data. Please try again.");
    } finally { setTableLoading(false); }
  }, [seedYearId]);

  useEffect(() => {
    if (selectedEntity) fetchReviewData(selectedEntity);
  }, [selectedEntity, fetchReviewData]);

  const handleMarkReviewed = async (mod) => {
    const submissionId = mod.submission_id ?? mod.id;
    setMarkingReviewedId(submissionId);
    try {
      await apiClient.post("/api/submit-review", {
        submission_id: submissionId, reporting_year_id: seedYearId,
      });
      fetchReviewData(selectedEntity);
    } catch (err) { console.error("Mark-reviewed error:", err); }
    finally { setMarkingReviewedId(null); }
  };
const selectedEntityName = legalEntities.find(
    (e) => e.entity_id == selectedEntity
  )?.entity_name;

  const handleSendBackConfirm = async (mod, comment) => {
    try {
      await apiClient.post("/api/change-requested", {
        submission_id: mod.submission_id ?? mod.id,
        reporting_year_id: seedYearId,
        reviewer_comment:  comment,
      });
      fetchReviewData(selectedEntity);
    } catch (err) { console.error("Send-back error:", err); }
  };

  const handleActionClick = (mod, label) => {
    const key = (label ?? "").toLowerCase().trim();
    if (key === "view") {
  navigate(mod.view_url, {
    state: {
      principle_id: mod.principle_id ?? mod.principleId,
      submission_id: mod.submission_id ?? mod.id,
      section_name: mod.principle_name ?? mod.module_name ?? mod.section_name,
      entityName: selectedEntityName,
      sourcePage: "review-screen",
      ...buildReviewState(),
    },
  });
} else if (key.includes("mark reviewed") || key.includes("mark review")) {
      handleMarkReviewed(mod);
    } else if (key.includes("send back")) {
      setSendBackTarget(mod);
    }
  };

  const entitySelected = !!selectedEntity;

  const hasAnyActions = modules.some((mod) => {
  const customActions = mod.actions ?? [];
  return (
    customActions.length > 0 ||
    !!mod.view_label ||
    !!mod.mark_reviewed_label ||
    !!mod.send_back_label
  );
});
//Access Permission
const { hasPermission } = usePermission();
  return (
    <div className="dash-page">
      <div className="dash-page-title">Review &amp; Validation</div>
      <div className="dash-page-subtitle">
        Review submitted sections, validate data, and either approve for next stage or send back for corrections.
      </div>

      {/* Filter card */}
      <div className="filter-card">
        <div className="filter-group">
          <label>Reporting Year</label>
          <input className="filter-input" value={seedYear || ""} disabled />
        </div>
        <div className="filter-group">
          <label>Legal Entity *</label>
          <select
            className="filter-input"
            value={selectedEntity}
            onChange={(e) => setSelectedEntity(e.target.value)}
            disabled={legalEntitiesLoading}
          >
            <option value="">{legalEntitiesLoading ? "Loading…" : "Select Legal Entity"}</option>
            {legalEntities.map((e) => (
              <option key={e.entity_id} value={e.entity_id}>{e.entity_name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Empty state */}
      {!entitySelected && (
        <div className="empty-entity">
          <div className="empty-entity__icon">🏢</div>
          <div className="empty-entity__title">Select a Legal Entity to continue</div>
          <div className="empty-entity__sub">
            Choose a legal entity from the filter above to load review data.
          </div>
        </div>
      )}

      {/* Content */}
      {entitySelected && (
        <>
          {/* Stage stat cards */}
          <div className="stats-row">
            {stageCounts.length > 0 && stageCounts.map((s) => {
                
                const faClass  = `fa-solid ${s.icon_name || "fa-circle"}`;
                return (
                  <div
                    key={s.status_code ?? s.status_name}
                    className="stat-card"
                    style={{
                      "--card-border": s.fore_color ?? "transparent",
                    }}
                  >
                    <div className="stat-icon" style={{ background: s.color_code, color: s.fore_color }}>
                      <i className={faClass} />
                    </div>
                    <div className="stat-body">
                      <span className="stat-title">{s.display_status_name || s.status_name}</span>
                      <span className="stat-value">{s.count ?? 0}</span>
                    </div>
                  </div>
               );
            })}
            
          </div>

          {/* Table card */}
          <div className="table-card">
            <div className="info_text">
             Showing all <strong>Submitted</strong> Principles Awaiting Review.
              
            </div>

            {tableLoading && (
              <div className="inline-state">
                <div className="spinner" />
                <span>Loading review data…</span>
              </div>
            )}

            {tableError && !tableLoading && (
              <div className="inline-state inline-state--error">
                <span>⚠</span> {tableError}
              </div>
            )}

            {!tableLoading && !tableError && (
              <table className="u-table">
                <thead>
                  <tr>
                    <th>PRINCIPLE</th>
                    <th>DESCRIPTION</th>
                    <th>SUBMITTED BY</th>
                    <th>SUBMITTED ON</th>
                    <th>STATUS</th>
                    <th>LAST UPDATED</th>
                    
                    {hasAnyActions && <th>ACTION</th>}
                  </tr>
                </thead>
                <tbody>
                  {modules.length === 0 ? (
                    <tr>
                      
                      <td colSpan={hasAnyActions ? 7 : 6} className="u-table-empty">
                        No modules awaiting review.
                      </td>
                    </tr>
                  ) : (
                    modules.map((mod) => {
                      const submissionId = mod.submission_id ?? mod.id;
                      const bgColor = mod.color_code ?? "#f3f4f6";
                      const fgColor = mod.fore_color ?? "#374151";
                      const statusIcon = mod.icon_name ?? "";

                      const rowActions = mod.actions ?? [
                        mod.view_label && { label: mod.view_label, color: mod.view_color ?? "#1E293B" },
                        mod.mark_reviewed_label && { label: mod.mark_reviewed_label, color: mod.mark_reviewed_color ?? "#3B82F6" },
                        mod.send_back_label && { label: mod.send_back_label, color: mod.send_back_color ?? "#B45309" },
                      ].filter(Boolean);

                      return (
                        <tr key={submissionId}>
                          <td className="td-code">{mod.principle_code ?? "—"}</td>
                          <td className="question-text">{mod.principle_name ?? mod.module_name ?? mod.section_name ?? "—"}</td>
                          <td className="td-muted">{mod.submitted_by_name ?? mod.submitted_by ?? "—"}</td>
                          <td className="td-date">{mod.submitted_at ?? "—"}</td>
                          <td>
                            <span
                              className="u-badge"
                              style={{ "--badge-bg": bgColor, "--badge-fg": fgColor }}
                            >
                              
                              <i className={`fa-solid ${statusIcon ?? "fa-solid fa-circle"} status_icon_size`} />
                              {mod.status ?? mod.status_name ?? "—"}
                            </span>
                          </td>
                          <td className="td-date">{mod.last_updated ?? "—"}</td>
                          
                          
                            {hasAnyActions && (
                            <td>
                              {rowActions.length > 0 && (
                                <div className="st-actions">
                                  {rowActions
                                    // Filter out everything except the "View" action
                                    .filter((action) => (action.label ?? "").toLowerCase().trim() === "view")
                                    .map((action) => {
                                      const key = (action.label ?? "").toLowerCase().trim();
                                      const isMarking = key.includes("mark") && markingReviewedId === submissionId;
                                      return (
                                        <button
                                          key={action.label}
                                          className="u-action-btn action" // Appended the custom .action class rule
                                          
                                          disabled={isMarking}
                                          onClick={() => handleActionClick(mod, action.label)}
                                        >
                                          {action.label+"->"}
                                        </button>
                                      );
                                    })}
                                </div>
                              )}
                            </td>
                          )}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            )}
          </div>

          {/* Footer */}
          <div className="ecf-footer">
            <button className="back-btn" onClick={() => navigate("/dashboard/submission-tracker", { state: { selectedEntity } })}>
              ← Back to Submission Tracker
            </button>
  { hasPermission("approval_lock") && (
            <button
              className="st-proceed-btn"
              onClick={() => navigate("/dashboard/approval-lock", { state: buildReviewState() })}
            >
              Proceed to Approval &amp; Lock →
            </button>
            )
}
          </div>
        </>
      )}

      {/* Send Back Modal */}
      {sendBackTarget && (
        <SendBackModal
          module={sendBackTarget}
          onClose={() => setSendBackTarget(null)}
          onConfirm={handleSendBackConfirm}
        />
      )}
    </div>
  );
};

export default ReviewScreen;
